import { FirebaseError } from 'firebase/app';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  deleteField,
  type DocumentData,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  address: string; // required from Sprint 2; may be empty string on legacy docs
  email?: string; // optional; stored normalized to lowercase
  registrationDate?: string; // ISO "YYYY-MM-DD", optional
  createdBy: string;
  isDeleted: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  deletedAt?: Timestamp;
}

export interface CreateCustomerData {
  firstName: string;
  lastName: string;
  phone: string;
  address: string; // required from Sprint 2
  email?: string; // optional
  registrationDate?: string; // ISO "YYYY-MM-DD", omit when not set
}

export interface UpdateCustomerData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  email?: string | null; // null = delete the field from Firestore
  registrationDate?: string | null; // null = delete the field from Firestore
}

export interface CustomerServiceError {
  code: string;
  message: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function docToCustomer(id: string, data: DocumentData): Customer {
  return {
    id,
    firstName: data.firstName as string,
    lastName: data.lastName as string,
    phone: data.phone as string,
    address: (data.address as string) ?? '',
    email: data.email as string | undefined,
    registrationDate: data.registrationDate as string | undefined,
    createdBy: data.createdBy as string,
    isDeleted: data.isDeleted as boolean,
    createdAt: data.createdAt as Timestamp,
    updatedAt: data.updatedAt as Timestamp,
    deletedAt: data.deletedAt as Timestamp | undefined,
  };
}

function toServiceError(error: unknown): CustomerServiceError {
  if (error instanceof FirebaseError) {
    return { code: error.code, message: error.message };
  }
  return { code: 'unknown', message: 'An unexpected error occurred' };
}

// ─── Service functions ────────────────────────────────────────────────────────

/** Returns all non-deleted customers in an org, newest first. */
export async function getAllCustomers(orgId: string): Promise<Customer[]> {
  try {
    const q = query(
      collection(db, 'orgs', orgId, 'customers'),
      where('isDeleted', '==', false),
      orderBy('createdAt', 'desc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => docToCustomer(d.id, d.data()));
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Returns a single customer by document ID, or null if not found. */
export async function getCustomerById(orgId: string, id: string): Promise<Customer | null> {
  try {
    const snapshot = await getDoc(doc(db, 'orgs', orgId, 'customers', id));
    if (!snapshot.exists()) return null;
    return docToCustomer(snapshot.id, snapshot.data());
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Returns true if no active customer in the org uses `email` (case-insensitive).
 * Pass `excludeId` to skip the current customer when checking on edit.
 */
export async function checkEmailUnique(orgId: string, email: string, excludeId?: string): Promise<boolean> {
  try {
    const normalized = email.toLowerCase();
    const q = query(
      collection(db, 'orgs', orgId, 'customers'),
      where('email', '==', normalized),
      where('isDeleted', '==', false),
      // limit(2): on the edit path one result may be the current customer (filtered out
      // below via excludeId), so we need room for that doc + one actual conflict.
      limit(2),
    );
    const snapshot = await getDocs(q);
    const matches = snapshot.docs.filter(d => d.id !== excludeId);
    return matches.length === 0;
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Creates a new customer document and returns the saved record. */
export async function createCustomer(
  orgId: string,
  createdBy: string,
  data: CreateCustomerData,
): Promise<Customer> {
  try {
    const { email, ...rest } = data;
    const ref = await addDoc(collection(db, 'orgs', orgId, 'customers'), {
      ...rest,
      ...(email ? { email: email.toLowerCase() } : {}),
      createdBy,
      isDeleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const snapshot = await getDoc(ref);
    return docToCustomer(snapshot.id, snapshot.data()!);
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Patches mutable fields on an existing customer. */
export async function updateCustomer(
  orgId: string,
  id: string,
  data: UpdateCustomerData,
): Promise<void> {
  try {
    const { registrationDate, email, ...rest } = data;
    const payload: Record<string, unknown> = { ...rest, updatedAt: serverTimestamp() };
    if (registrationDate !== undefined) {
      payload.registrationDate = registrationDate === null ? deleteField() : registrationDate;
    }
    if (email !== undefined) {
      payload.email = email === null ? deleteField() : email.toLowerCase();
    }
    await updateDoc(doc(db, 'orgs', orgId, 'customers', id), payload);
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Soft-deletes a customer (sets isDeleted + deletedAt). */
export async function deleteCustomer(orgId: string, id: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'orgs', orgId, 'customers', id), {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Subscribes to real-time updates for all active customers in an org.
 * Returns an unsubscribe function to clean up the listener.
 */
export function subscribeToCustomers(
  orgId: string,
  onData: (_customers: Customer[]) => void,
  onError: (_error: CustomerServiceError) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'orgs', orgId, 'customers'),
    where('isDeleted', '==', false),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(
    q,
    snapshot => onData(snapshot.docs.map(d => docToCustomer(d.id, d.data()))),
    err => onError(toServiceError(err)),
  );
}
